/**
 * Address Validation Rules and Utilities
 *
 * Provides validation patterns and helper functions for address field validation.
 * These patterns can be used by consuming applications to implement field validation.
 */

import { addressFormMessages } from './address-form-messages';
import { generateAddressFormSchema, isFieldRequired } from './schema-generator';

import type { AddressFieldSchema, AddressFormSchemaOptions } from './types';
import type { MessageDescriptor } from 'react-intl';

/**
 * Common validation patterns for address fields
 */
export const VALIDATION_PATTERNS = {
  /**
   * US Zip Code: 5 digits, or 5+4 with an optional hyphen or space separator.
   * Accepts `787011234` (no separator) because marketplaces omit the hyphen.
   * No `g` flag — this regex is shared at module level so .test() must be stateless.
   */
  US_ZIP: /^\d{5}(?:[-\s]?\d{4})?$/,

  /** Canadian Postal Code: A1A 1A1 format */
  CA_POSTAL_CODE: /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/,

  /** UK Postcode: Various formats */
  UK_POSTCODE: /^[A-Z]{1,2}\d{1,2}[A-Z]?\s?\d[A-Z]{2}$/i,

  /** Email format */
  EMAIL: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,

  /** Phone number (basic - allows various formats) */
  PHONE: /^[\d\s()+-]+$/,

  /** Alphanumeric with spaces and common punctuation */
  ALPHANUMERIC_EXTENDED: /^[a-zA-Z0-9\s.,#'-]*$/,
} as const;

/**
 * Validation message keys for internationalization
 *
 * These keys should be used with react-intl or other i18n libraries.
 * Consuming applications are responsible for providing translations.
 */
export const VALIDATION_MESSAGE_KEYS = {
  REQUIRED: 'validation.required',
  INVALID_EMAIL: 'validation.email.invalid',
  INVALID_PHONE: 'validation.phone.invalid',
  INVALID_ZIP_CODE: 'validation.zipCode.invalid',
  INVALID_POSTAL_CODE: 'validation.postalCode.invalid',
  INVALID_VALUE: 'validation.value.invalid',
  MAX_LENGTH: 'validation.maxLength',
  MIN_LENGTH: 'validation.minLength',
} as const;

/**
 * Get validation pattern for postal code based on country
 * @param {string} countryCode - ISO 3166-1 alpha-2 country code
 * @returns {RegExp | undefined} RegExp pattern for postal code validation, or undefined if no specific pattern
 */
export const getPostalCodePattern = (countryCode: string): RegExp | undefined => {
  switch (countryCode?.toUpperCase()) {
    case 'US':
      return VALIDATION_PATTERNS.US_ZIP;
    case 'CA':
      return VALIDATION_PATTERNS.CA_POSTAL_CODE;
    case 'GB':
      return VALIDATION_PATTERNS.UK_POSTCODE;
    default:
      return undefined; // No specific pattern - allow any value
  }
};

/**
 * Get validation error message key for a field
 *
 * Returns an i18n message key that should be translated by the consuming application.
 * Use with react-intl's formatMessage or similar i18n utilities.
 * @param {AddressFieldSchema} field - Field schema
 * @param {string} validationType - Type of validation that failed
 * @returns {string} i18n message key (e.g., 'validation.required')
 * @example
 * const messageKey = getValidationErrorMessageKey(field, 'required');
 * const errorMessage = intl.formatMessage({ id: messageKey });
 */
export const getValidationErrorMessageKey = (field: AddressFieldSchema, validationType: string): string => {
  switch (validationType) {
    case 'required':
      return VALIDATION_MESSAGE_KEYS.REQUIRED;
    case 'email':
      return VALIDATION_MESSAGE_KEYS.INVALID_EMAIL;
    case 'phone':
      return VALIDATION_MESSAGE_KEYS.INVALID_PHONE;
    case 'pattern':
      if (field.type === 'postalCode') {
        return VALIDATION_MESSAGE_KEYS.INVALID_POSTAL_CODE;
      }
      return VALIDATION_MESSAGE_KEYS.INVALID_VALUE;
    default:
      return VALIDATION_MESSAGE_KEYS.INVALID_VALUE;
  }
};

/**
 * Maximum lengths for address fields
 */
export const FIELD_MAX_LENGTHS = {
  company: 100,
  email: 100,
} as const;

/**
 * Get max length for a field
 * @param {string} fieldName - Name of the field
 * @returns {number | undefined} Maximum character length
 */
export const getFieldMaxLength = (fieldName: string): number | undefined => {
  return FIELD_MAX_LENGTHS[fieldName as keyof typeof FIELD_MAX_LENGTHS];
};

// ---------------------------------------------------------------------------
// Field-level error type and validator
// ---------------------------------------------------------------------------

export type AddressFieldError = {
  /** Address field name (e.g. 'postalCode'). Matches AddressForm's `errors` keys. */
  field: string;
  message: MessageDescriptor;
  values?: Record<string, string | number>;
  /**
   * True when the value is present but malformed, as opposed to merely missing.
   * Callers surface these immediately — a malformed value can arrive from a
   * hydrated marketplace order the user never typed, so waiting for blur would
   * leave the form silently broken.
   */
  isInvalidValue?: boolean;
};

/**
 * Subset of AddressFormSchemaOptions relevant to validation. Passed through to
 * `generateAddressFormSchema` so the validator and the form walk the same field set.
 */
export type AddressValidationOptions = Pick<
  AddressFormSchemaOptions,
  'hiddenFields' | 'requiredFields' | 'requireAll' | 'showAddressLine3'
>;

const getFieldError = (field: AddressFieldSchema, rawValue: unknown, countryCode: string): AddressFieldError | null => {
  const text = typeof rawValue === 'string' ? rawValue.trim() : '';

  // Empty value — required check only. Never also a format error (they're mutually exclusive).
  if (text === '') {
    if (isFieldRequired(field)) {
      return { field: field.name, message: addressFormMessages.required };
    }
    return null;
  }

  const maxLength = getFieldMaxLength(field.name);
  if (maxLength !== undefined && text.length > maxLength) {
    return {
      field: field.name,
      message: addressFormMessages.maxLength,
      values: { max: maxLength },
      isInvalidValue: true,
    };
  }

  if (field.type === 'postalCode') {
    const pattern = getPostalCodePattern(countryCode);
    if (pattern !== undefined && !pattern.test(text)) {
      return {
        field: field.name,
        message: addressFormMessages.invalidPostalCode,
        isInvalidValue: true,
      };
    }
  }

  const hasEmailRule = field.type === 'email' || field.validation.some((r) => r.type === 'email');
  if (hasEmailRule && !VALIDATION_PATTERNS.EMAIL.test(text)) {
    return { field: field.name, message: addressFormMessages.invalidEmail, isInvalidValue: true };
  }

  // Phone format is deliberately not validated — see plan decision §3.
  return null;
};

/**
 * Returns at most one error per field. Pass the same `options` you pass to
 * `<AddressForm>` so the validator and the form walk identical field sets —
 * if they diverge, a blocked field may have no visible error.
 */
export const getAddressFieldErrors = (
  value: Readonly<Record<string, unknown>>,
  countryCode: string,
  options?: AddressValidationOptions,
): AddressFieldError[] => {
  const schema = generateAddressFormSchema(countryCode, options);
  return schema.fields
    .map((field) => getFieldError(field, value[field.name], countryCode))
    .filter((error): error is AddressFieldError => error !== null);
};
